/**
 * Shared helpers for session-storage-sync.js
 * Centralizes the cross-tab read/write of sessionStorage.
 *
 * Sync model: DENYLIST, not allowlist. Every sessionStorage key is synced
 * across tabs EXCEPT the ones listed in TAB_LOCAL_KEYS. A hand-maintained
 * allowlist was the previous approach and it silently rotted — new/renamed keys
 * (the admin route-path cache, a rotated refresh token) stopped syncing without
 * anyone noticing. Sync-by-default means a new session key is covered
 * automatically; opting a key OUT is now the deliberate, visible act.
 */

/**
 * Keys that must NOT propagate across tabs — genuinely tab-local state only.
 * Empty today: every current session key (auth tokens, user, features, admin
 * path cache, user preferences, the client-portal analytics session id) is
 * meant to be consistent for the whole browser session.
 *
 * `type` and `timestamp` are the sync-message envelope fields, kept out of
 * sessionStorage on the receiving side (see storeSessionData).
 */
const TAB_LOCAL_KEYS = [];
const ENVELOPE_KEYS = ['type', 'timestamp'];

/**
 * Read syncable sessionStorage keys into a broadcast payload.
 *
 * `includeKeys` scopes the payload to a specific set of keys. This is how a
 * FEATURES/PATHS change broadcasts WITHOUT carrying auth tokens: a stale token
 * snapshot broadcast by a frequent, non-auth event (a feature toggle) would
 * otherwise overwrite a sibling tab's freshly-rotated pair and log it out. Auth
 * events (token refresh, new-tab bootstrap) still broadcast the full payload.
 *
 * @param {string} type - Message type (e.g. 'SESSION_DATA', 'LOGOUT')
 * @param {string[]|null} includeKeys - When set, only these keys are included.
 * @returns {Object} Payload with type, the selected session values, and timestamp
 */
export function getSessionPayload(type = 'SESSION_DATA', includeKeys = null) {
  const payload = { type, timestamp: Date.now() };
  for (let i = 0; i < sessionStorage.length; i += 1) {
    const key = sessionStorage.key(i);
    if (!key || TAB_LOCAL_KEYS.includes(key) || ENVELOPE_KEYS.includes(key)) {
      continue;
    }
    if (includeKeys && !includeKeys.includes(key)) {
      continue;
    }
    payload[key] = sessionStorage.getItem(key);
  }
  return payload;
}

/**
 * Write incoming session data into sessionStorage.
 * Writes every key in the payload except the message envelope and tab-local
 * keys. Only truthy values are written — an absent/empty value never clears a
 * key here (logout clears via its own dedicated path).
 * @param {Object} data - Incoming session payload
 */
export function storeSessionData(data) {
  Object.keys(data).forEach((key) => {
    if (
      TAB_LOCAL_KEYS.includes(key) ||
      ENVELOPE_KEYS.includes(key) ||
      !data[key]
    ) {
      return;
    }
    sessionStorage.setItem(key, data[key]);
  });
}
